import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";

import {
  Box,
  FormControlLabel,
  Button,
  Chip,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";

import { ValuesSelected } from "../../../redux/slices/dataTable/dataTableFilters";
import { AutocompleteStyles } from "../../dataTable/table/MenuOptions/options/filters/filtersSideBar/autoCompleteFilters/AutoCompleteStyles";

import { useTranslation } from "react-i18next";
import { useRef, useState, useCallback, useMemo } from "react";

// Memoize icons
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface AutocompleteDashboardFiltersProps {
  typeOfAutocomplete: string;
  dataOptions: any[];
  allValuesSelected: boolean;
  valuesSelected: ValuesSelected[];
  loading: boolean;
  setSelectAll: (values: any[]) => void;
  resetSelectAll: () => void;
  managingSelectOption: (values: ValuesSelected[]) => void;
  fetchActivityChartValues: () => void;
}

const AutocompleteDashboardFilters = ({
  typeOfAutocomplete,
  dataOptions,
  allValuesSelected,
  valuesSelected,
  loading,
  setSelectAll,
  resetSelectAll,
  managingSelectOption,
  fetchActivityChartValues
}: AutocompleteDashboardFiltersProps) => {
  const [t] = useTranslation("global");
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const hasInitialized = useRef(false);

  // Memoizar las funciones para evitar re-renders
  const TypeOfLabel = useCallback(() => {
    if (typeOfAutocomplete === "locations") {
      return t("Data.Search_sites");
    }
    return "";
  }, [typeOfAutocomplete, t]);

  const filterName = useCallback(() => {
    if (typeOfAutocomplete === "locations") {
      return t("Data.Sites");
    }
    return "";
  }, [typeOfAutocomplete, t]);

  const transform = useMemo(
    () => (dataOptions.length >= 1 ? dataOptions : []),
    [dataOptions]
  );

  
  const getUniqueKey = useCallback((option: any, index?: number) => {
    if (typeOfAutocomplete === "actions") {
      return (
        option.userFacingNameTextId ||
        option.id ||
        `${option.name}_${index || 0}`
      );
    }
    return option.id || `${option.name}_${index || 0}`;
  }, [typeOfAutocomplete]);

  const filteredDataOptions = useMemo(() => dataOptions, [dataOptions]);

  React.useEffect(() => {
    if (dataOptions.length > 0 && !hasInitialized.current) {
      if (valuesSelected.length === 0 && !allValuesSelected && !loading) {
        setSelectAll(transform);
      }
      hasInitialized.current = true;
    }
  }, [
    dataOptions.length,
    valuesSelected.length,
    allValuesSelected,
    loading,
    setSelectAll,
    transform,
  ]);

  React.useEffect(() => {
    const hasValues = valuesSelected.length > 0;
    const shouldShowMenu = hasValues || autocompleteOpen;

    if (shouldShowMenu && autocompleteRef.current) {
      const rect = autocompleteRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;

      const topOffset = typeOfAutocomplete === "actions" ? -150 : 0;

      if (screenWidth <= 700) {
        setMenuPosition({
          top: rect.bottom + 420,
          left: rect.left,
        });
      } else {
        setMenuPosition({
          top: rect.top + topOffset,
          left: rect.left - 360,
        });
      }
    }
  }, [typeOfAutocomplete, valuesSelected.length, autocompleteOpen]);

  const handleRemoveChip = useCallback((optionToRemove: ValuesSelected) => {
    const newValues = valuesSelected?.filter((item) => item.id !== optionToRemove.id) || [];
    managingSelectOption(newValues);
  }, [valuesSelected, managingSelectOption]);
  const sideMenuStyle = useMemo(() => ({
    ...AutocompleteStyles.sideMenuStyle,
    top: menuPosition.top,
    left: menuPosition.left,
  }), [menuPosition.top, menuPosition.left]);

  return (
    <div style={{ position: "relative" }}>
      <div ref={autocompleteRef}></div>
      <Autocomplete
        sx={{
          ...AutocompleteStyles.Autocomplete,
          overflowY: valuesSelected?.length > 2 ? "scroll" : "hidden",
          height: valuesSelected?.length > 2 ? "80px" : "60px",
        }}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        filterOptions={useMemo(() => {
          const filter = createFilterOptions<ValuesSelected>();
          return (options: ValuesSelected[], params: any) => {
            const filtered = filter(options, params);
            return [
              { name: t("Dashboard.setAllLocations"), id: 0 } as ValuesSelected,
              ...filtered,
            ];
          };
        }, [t])}
        disabled={loading}
        className="autocomplete-dashboard mb-5"
        multiple
        loading={loading}
        disableListWrap={true}
        options={filteredDataOptions}
        disableCloseOnSelect
        value={valuesSelected}
        getOptionLabel={(option: ValuesSelected) => {
          return option.name;
        }}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id;
        }}
        onChange={useCallback((_e: any, value: ValuesSelected[], reason: string) => {
          if (reason === "clear") {
            resetSelectAll();
            return;
          }

          if (reason === "removeOption" && allValuesSelected) {
            resetSelectAll();
            managingSelectOption(value);
            return;
          }

          if (reason === "selectOption" && allValuesSelected) {
            resetSelectAll();
            managingSelectOption(value);
            return;
          }

          if (
            reason === "selectOption" &&
            value.length === filteredDataOptions.length
          ) {
            setSelectAll(
              typeOfAutocomplete === "actions" ? filteredDataOptions : transform
            );
          }

          managingSelectOption(value);
        }, [allValuesSelected, filteredDataOptions, resetSelectAll, managingSelectOption, setSelectAll, typeOfAutocomplete, transform])}
        renderInput={useCallback((params: any) => (
          <TextField
            sx={AutocompleteStyles.AutoCompleteTextField}
            {...params}
            placeholder={TypeOfLabel()}
            InputProps={{
              ...params.InputProps,
            }}
          />
        ), [TypeOfLabel])}
        renderOption={(props, option, { selected }) => {
          if (option.id === 0) {
            return (
              <Box
                key={option.id}
                sx={[
                  AutocompleteStyles.BoxOptions,
                  {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  },
                ]}
                onMouseDown={(e) => e.preventDefault()}
                pl={1.5}
                py={0.5}
              >
                <FormControlLabel
                  onClick={(e) => {
                    if (filteredDataOptions.length >= 1) {
                      e.preventDefault();
                      if (allValuesSelected) {
                        resetSelectAll();
                      } else {
                        setSelectAll(transform);
                      }
                    }
                  }}
                  disabled={filteredDataOptions.length === 0 || loading}
                  label={
                    allValuesSelected
                      ? t("filters.allSites")
                      : t("Dashboard.setAllLocations")
                  }
                  control={
                    <Checkbox
                      disabled={filteredDataOptions.length === 0 || loading}
                      checked={allValuesSelected}
                    />
                  }
                />

                <Button
                  onClick={() => {
                    fetchActivityChartValues();
                  }}
                  disabled={
                    loading ||
                    (valuesSelected.length === 0 && !allValuesSelected)
                  }
                  variant="contained"
                  sx={{
                    marginRight: "2px",
                    textTransform: "none",
                  }}
                >
                  {t("Common_options.Apply")}
                </Button>
              </Box>
            );
          }

          if (loading) {
            return (
              <div className="circular-progress-components">
                <CircularProgress />
              </div>
            );
          } else {
            return (
              <li {...props} key={getUniqueKey(option)}>
                <Checkbox
                  icon={icon}
                  disabled={loading}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={
                    option.id === 0
                      ? (valuesSelected?.filter((x) => x.id === undefined).length === dataOptions.length)
                      : selected
                  }
                />

                <div>{`${option.name}`}</div>
              </li>
            );
          }
        }}
        renderTags={(list) => {
          if (allValuesSelected && list.length === 0) {
            return (
              <Box sx={AutocompleteStyles.BoxRenderTags}>
                <Chip
                  label={t("filters.allSites")}
                  color="primary"
                  variant="filled"
                />
              </Box>
            );
          }

          if (
            list.length === filteredDataOptions.length &&
            filteredDataOptions.length > 0
          ) {
            return (
              <Box sx={AutocompleteStyles.BoxRenderTags}>
                <Chip
                  label={t("filters.allSites")}
                  color="primary"
                  variant="filled"
                />
              </Box>
            );
          }

          if (list.length === 1) {
            return (
              <Box sx={AutocompleteStyles.BoxRenderTags}>
                {list.map((value: any, index) => (
                  <Chip
                    key={getUniqueKey(value, index)}
                    label={
                      value.name.length > 10
                        ? `${value.name.slice(0, 13)}...`
                        : value.name
                    }
                  />
                ))}
              </Box>
            );
          }

          if (list.length > 1) {
            return (
              <Box sx={AutocompleteStyles.BoxRenderTags}>
                <Chip
                  label={`${
                    list[0].name.length > 10
                      ? `${list[0].name.slice(0, 13)}...`
                      : list[0].name
                  } ${
                    list.length >= 2
                      ? `+ ${list.length - 1} ${t("Common_options.others")}`
                      : ""
                  }  `}
                />
              </Box>
            );
          }
        }}
        renderGroup={(params) => params as unknown as React.ReactNode}
      />

      {autocompleteOpen &&
        valuesSelected &&
        valuesSelected.length > 0 &&
        menuPosition.top !== 0 &&
        menuPosition.left !== 0 && (
          <Paper
            sx={sideMenuStyle}
            elevation={3}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" component="h2">
                {filterName()}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: "300px",
                overflow: "auto",
                flex: 1,
                paddingRight: 1,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
            >
              {valuesSelected &&
                valuesSelected.length > 0 &&
                valuesSelected.map((option, index) => (
                  <Chip
                    key={getUniqueKey(option, index)}
                    label={option.name}
                    onDelete={() => handleRemoveChip(option)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
            </Box>
          </Paper>
        )}
    </div>
  );
};
export default AutocompleteDashboardFilters;
